/**
 * @file MatchingPopulationDrawer.jsx
 * @module ui/src/components/tenant-admin/MatchingPopulationDrawer
 * @description
 *   Full runtime population browser for the Assignment Scope section.
 *   Renders server-side filtered, cursor-paginated positions that currently
 *   match the plan's Assignment Scope rules, with per-position rule-match
 *   reasons, additions/removals since last resolution, and population
 *   freshness. Delegates population browsing on behalf of C-18
 *   AssignmentScopeSection so that component does not become a
 *   30,000-position data browser.
 *
 * @version 1.0.1
 * @since S754
 * @author data-fit SME Build · Reviewed: Claude S754 · Corrected: Claude S756
 *
 * @architecture
 *   Locked decisions : #1, #9, #19, #25
 *   Temporal invariants : INV-3, INV-5
 *   Design principles : DP-3, DP-5, DP-14
 *   Cross-cutting contracts : XC-3, XC-4, XC-7
 *
 * @domain-rules
 *   - Population change does not amend the plan; Assignment Scope rules are
 *     plan-governed but population membership is runtime-resolved.
 *   - Population is fetched server-side with cursor pagination; no client-side
 *     filtering or virtualization is performed here.
 *   - Freshness is sourced from the server meta payload; the component never
 *     computes staleness independently (XC-3).
 *   - Additions/removals since last resolution are surfaced explicitly so
 *     admins can detect scope drift without triggering an amendment.
 *   - The raw scopeRules prop is plumbing only; it is never displayed to the
 *     user — not as JSON, not as debug output, not in any form (OI-S754-C54-SCOPE-JSON-01).
 *
 * @session-6-migration-flags
 *   - none — all fields consumed are present in ddl_summary_r57 (position,
 *     plan_assignment, data_readiness_status). Population meta endpoint shape
 *     is a Session 6 API contract gap.
 *
 * @changelog
 *   1.0.0  S754  Initial build — Group B batch delivery
 *   1.0.1  S756  OI-S754-C54-LOADMORE-ABORT-01 — loadMore AbortController now
 *                stored in loadMoreControllerRef; aborted on drawer close/unmount
 *                via useEffect cleanup so in-flight page requests cannot update
 *                state after the drawer has closed.
 *                OI-S754-C54-SCOPE-JSON-01 — removed raw JSON scopeRules debug
 *                block (<details><pre>JSON.stringify</pre></details>) entirely;
 *                scopeRules prop is API plumbing only and must never render.
 *                Full data-fit production file standard applied (S756).
 */

import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import DataFreshnessIndicator from "../shared/DataFreshnessIndicator";

// WHY: Focusable selector is defined once and shared by the focus-trap
// useEffect so the drawer is keyboard-navigable without a third-party library.
const FOCUSABLE = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * WHY: normalizePosition maps both camelCase API responses and snake_case
 * legacy payloads to a single internal shape, preventing field-name drift
 * from causing silent undefined renders.
 */
function normalizePosition(item) {
  return {
    positionId: item.positionId ?? item.position_id,
    positionName: item.positionName ?? item.position_name ?? "Unnamed position",
    employeeId: item.employeeId ?? item.employee_id ?? "—",
    role: item.role ?? item.role_name ?? "—",
    segment: item.segment ?? item.segment_name ?? "—",
    businessUnit: item.businessUnit ?? item.business_unit ?? "—",
    region: item.region ?? item.region_name ?? "—",
    matchStatus: item.matchStatus ?? item.match_status ?? "matched",
    matchReasons: item.matchReasons ?? item.match_reasons ?? [],
    effectiveDate: item.effectiveDate ?? item.effective_date ?? null,
    holdStatus: item.holdStatus ?? item.hold_status ?? null,
  };
}

/**
 * @typedef {Object} MatchingPopulationDrawerProps
 * @property {number} planId
 * @property {Object} scopeRules  - Assignment Scope rule payload; used as an
 *   API parameter only. Never rendered to the user.
 * @property {boolean} open
 * @property {Function} onClose
 */

/**
 * Full runtime population browser for the plan's Assignment Scope.
 *
 * @param {MatchingPopulationDrawerProps} props
 * @returns {JSX.Element|null}
 */
export default function MatchingPopulationDrawer({
  planId,
  scopeRules,   // eslint-disable-line no-unused-vars — API plumbing; never rendered
  open,
  onClose,
}) {
  const titleId = useId();
  const drawerRef = useRef(null);
  const closeRef = useRef(null);
  const previousFocusRef = useRef(null);

  // WHY: loadMoreControllerRef holds the AbortController for any in-flight
  // "load more" fetch. It is stored in a ref (not state) so the cleanup
  // function in the close/unmount effect can abort it without triggering a
  // re-render. Without this, a slow load-more response can call setState
  // after the drawer is unmounted, producing a React no-op warning and
  // potentially corrupting position state. (OI-S754-C54-LOADMORE-ABORT-01)
  const loadMoreControllerRef = useRef(null);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    role: "",
    segment: "",
    matchStatus: "",
  });
  const [pageCursor, setPageCursor] = useState(null);
  const [positions, setPositions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedPositionId, setExpandedPositionId] = useState(null);
  const [populationMeta, setPopulationMeta] = useState(null);

  // WHY: loadMeta fetches freshness and resolution-delta metadata separately
  // from the position list so the freshness indicator renders immediately
  // without waiting for potentially large position payloads.
  const loadMeta = useCallback(async (signal) => {
    setMetaLoading(true);
    try {
      const response = await fetch(
        `/api/v1/plans/${encodeURIComponent(planId)}/population/meta`,
        { signal }
      );
      if (!response.ok) {
        throw new Error(`Unable to load population metadata (${response.status}).`);
      }
      const payload = await response.json();
      setPopulationMeta({
        resolvedAt: payload.resolvedAt ?? payload.resolved_at ?? null,
        additionCount:
          payload.additionCount ??
          payload.additions_since_last_resolution ??
          0,
        removalCount:
          payload.removalCount ??
          payload.removals_since_last_resolution ??
          0,
        freshnessStatus:
          payload.freshnessStatus ??
          payload.freshness_status ??
          "warning",
      });
    } catch (err) {
      if (err.name !== "AbortError") {
        setError(err.message || "Unable to load population metadata.");
      }
    } finally {
      if (!signal.aborted) setMetaLoading(false);
    }
  }, [planId]);

  // WHY: loadPositions is memoized on filter/search/planId so the debounced
  // useEffect below re-runs only when those inputs change rather than on every
  // render. Server-side pagination means the client never filters or slices.
  const loadPositions = useCallback(
    async (cursor, append, signal) => {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({ limit: "50" });
      if (search.trim()) params.set("q", search.trim());
      if (filters.role) params.set("role", filters.role);
      if (filters.segment) params.set("segment", filters.segment);
      if (filters.matchStatus) params.set("status", filters.matchStatus);
      if (cursor) params.set("cursor", cursor);

      try {
        const response = await fetch(
          `/api/v1/plans/${encodeURIComponent(planId)}/population?${params.toString()}`,
          { signal }
        );

        if (!response.ok) {
          throw new Error(`Unable to load matching population (${response.status}).`);
        }

        const payload = await response.json();
        // WHY: EP30 spec returns 'members' array (api_spec_fileB §2.2).
        // payload.items is a legacy alias kept as fallback.
        const rows = (payload.members ?? payload.items ?? []).map(normalizePosition);

        setPositions((current) => append ? [...current, ...rows] : rows);
        setTotalCount(payload.totalCount ?? payload.total_count ?? 0);
        setNextCursor(payload.nextCursor ?? payload.next_cursor ?? null);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Unable to load matching population.");
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [filters, planId, search]
  );

  // WHY: Meta is fetched once when the drawer opens and aborted on close.
  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();
    loadMeta(controller.signal);
    return () => controller.abort();
  }, [open, loadMeta]);

  // WHY: Position fetch is debounced 200ms so rapid filter changes do not
  // fire multiple in-flight requests. The AbortController cancels any
  // in-flight request when the effect re-runs or the drawer closes.
  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setPageCursor(null);
      setExpandedPositionId(null);
      loadPositions(null, false, controller.signal);
    }, 200);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, loadPositions]);

  // WHY: Focus trap and focus restoration. On open, focus moves to the close
  // button. On close/unmount, focus returns to whichever element triggered the
  // drawer. The cleanup also aborts any in-flight loadMore request so a slow
  // page response cannot call setState after the component is gone.
  // (OI-S754-C54-LOADMORE-ABORT-01)
  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;
    window.requestAnimationFrame(() => closeRef.current?.focus());

    const keyHandler = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) return;
      const controls = Array.from(drawerRef.current.querySelectorAll(FOCUSABLE));
      if (!controls.length) return;

      const first = controls[0];
      const last = controls[controls.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", keyHandler);

    return () => {
      document.removeEventListener("keydown", keyHandler);

      // WHY: Abort any in-flight loadMore request so it cannot call setState
      // after the drawer has closed. Without this abort, a slow response
      // resolves against unmounted state. (OI-S754-C54-LOADMORE-ABORT-01)
      if (loadMoreControllerRef.current) {
        loadMoreControllerRef.current.abort();
        loadMoreControllerRef.current = null;
      }

      const previous = previousFocusRef.current;
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  // WHY: loadMore stores its AbortController in loadMoreControllerRef so the
  // close/unmount cleanup above can abort it. Each new loadMore call first
  // aborts any still-running prior loadMore request to prevent overlapping
  // page fetches from racing. (OI-S754-C54-LOADMORE-ABORT-01)
  const loadMore = () => {
    if (!nextCursor || loading) return;

    // Abort any previous loadMore that has not yet resolved.
    if (loadMoreControllerRef.current) {
      loadMoreControllerRef.current.abort();
    }

    const controller = new AbortController();
    loadMoreControllerRef.current = controller;

    setPageCursor(nextCursor);
    loadPositions(nextCursor, true, controller.signal);
  };

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgb(var(--color-text) / 0.28)",
        zIndex: 50,
      }}
    >
      <section
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="modifier-manager-card"
        tabIndex={-1}
        style={{
          position: "absolute",
          insetBlock: 0,
          insetInlineEnd: 0,
          width: "min(900px, 100%)",
          background: "rgb(var(--color-surface))",
          borderInlineStart: "1px solid rgb(var(--color-border))",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header className="modifier-manager-header">
          <div>
            <h2 id={titleId}>Matching population</h2>
            <p>
              Runtime-resolved positions matching the current Assignment
              Scope rules.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="icon-button"
            aria-label="Close matching population"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="modifier-manager-body">
          {/* WHY: The population-change-does-not-amend notice is locked copy
               required by DP-3: governed scope rules and runtime population
               are always visibly separate. */}
          <div className="modifier-manager-info">
            <strong>
              A change in the matching population does not amend this
              plan. Population is resolved at runtime from the Assignment
              Scope rules.
            </strong>
          </div>

          <section
            className="modifier-manager-card"
            aria-label="Population resolution status"
          >
            <div className="modifier-manager-body">
              {metaLoading ? (
                <div className="skeleton" style={{ height: 44 }} />
              ) : (
                populationMeta && (
                  <>
                    {/* WHY: DataFreshnessIndicator is the shared freshness
                         signal — this component never computes staleness
                         independently (XC-3). */}
                    <DataFreshnessIndicator
                      signal="population"
                      timestamp={populationMeta.resolvedAt}
                      status={populationMeta.freshnessStatus}
                    />
                    <dl>
                      <dt>Additions since last resolution</dt>
                      <dd>{populationMeta.additionCount}</dd>
                      <dt>Removals since last resolution</dt>
                      <dd>{populationMeta.removalCount}</dd>
                    </dl>
                  </>
                )
              )}
            </div>
          </section>

          <div
            role="group"
            aria-label="Population filters"
            style={{ display: "flex", gap: 12, flexWrap: "wrap" }}
          >
            <label>
              <span>Position name</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ display: "block", marginBlockStart: 4, padding: "4px 8px", borderRadius: 4, border: "1px solid rgb(var(--color-border))", background: "rgb(var(--color-surface))", color: "rgb(var(--color-text))", fontSize: 13, width: "100%" }}
              />
            </label>

            <label>
              <span>Role</span>
              <input
                value={filters.role}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    role: event.target.value,
                  }))
                }
                style={{ display: "block", marginBlockStart: 4, padding: "4px 8px", borderRadius: 4, border: "1px solid rgb(var(--color-border))", background: "rgb(var(--color-surface))", color: "rgb(var(--color-text))", fontSize: 13, width: "100%" }}
              />
            </label>

            <label>
              <span>Segment</span>
              <input
                value={filters.segment}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    segment: event.target.value,
                  }))
                }
                style={{ display: "block", marginBlockStart: 4, padding: "4px 8px", borderRadius: 4, border: "1px solid rgb(var(--color-border))", background: "rgb(var(--color-surface))", color: "rgb(var(--color-text))", fontSize: 13, width: "100%" }}
              />
            </label>

            <label>
              <span>Match status</span>
              <select
                value={filters.matchStatus}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    matchStatus: event.target.value,
                  }))
                }
                style={{ display: "block", marginBlockStart: 4, padding: "4px 8px", borderRadius: 4, border: "1px solid rgb(var(--color-border))", background: "rgb(var(--color-surface))", color: "rgb(var(--color-text))", fontSize: 13 }}
              >
                <option value="">All statuses</option>
                <option value="matched">Current matches</option>
                <option value="added">Added</option>
                <option value="removed">Removed</option>
              </select>
            </label>
          </div>

          {error && (
            <div className="modifier-manager-error-state" role="alert">
              <strong>Population unavailable</strong>
              <p>{error}</p>
            </div>
          )}

          {loading && positions.length === 0 && (
            <div aria-busy="true">
              {[0, 1, 2, 3, 4].map((index) => (
                <div
                  key={index}
                  className="skeleton"
                  style={{ height: 60, marginBlockEnd: 8 }}
                />
              ))}
            </div>
          )}

          {!loading && !error && positions.length === 0 && (
            <div className="modifier-manager-empty-state">
              <strong>
                No positions currently match the assignment scope rules.
              </strong>
              <p>
                Close this drawer to return to the Assignment Scope rules
                configuration.
              </p>
            </div>
          )}

          {positions.length > 0 && (
            <div>
              <p>
                Showing {positions.length} of {totalCount} positions
              </p>

              {positions.map((position) => {
                const expanded = expandedPositionId === position.positionId;

                return (
                  <article
                    key={position.positionId}
                    className="modifier-manager-card"
                    style={{ marginBlockEnd: 8 }}
                  >
                    <header className="modifier-manager-header">
                      <div>
                        <strong>{position.positionName}</strong>
                        <p>
                          {position.role} · {position.segment}
                        </p>
                        {/* WHY: Match status is named in text and a CSS class
                             reinforces it visually — never color alone (XC-7/DP-14). */}
                        <span
                          className={
                            position.matchStatus === "added"
                              ? "modifier-manager-badge--complete"
                              : position.matchStatus === "removed"
                                ? "modifier-manager-badge--incomplete"
                                : "modifier-manager-badge--readonly"
                          }
                        >
                          {position.matchStatus === "added"
                            ? "Added since last resolution"
                            : position.matchStatus === "removed"
                              ? "Removed since last resolution"
                              : "Current match"}
                        </span>
                      </div>

                      <button
                        type="button"
                        className="secondary-button"
                        aria-expanded={expanded}
                        onClick={() =>
                          setExpandedPositionId((current) =>
                            current === position.positionId
                              ? null
                              : position.positionId
                          )
                        }
                      >
                        {expanded ? "Hide details" : "View details"}
                      </button>
                    </header>

                    {expanded && (
                      <div className="modifier-manager-body">
                        <dl>
                          <dt>Employee ID</dt>
                          <dd>{position.employeeId}</dd>
                          <dt>Role</dt>
                          <dd>{position.role}</dd>
                          <dt>Segment</dt>
                          <dd>{position.segment}</dd>
                          <dt>Business unit</dt>
                          <dd>{position.businessUnit}</dd>
                          <dt>Region</dt>
                          <dd>{position.region}</dd>
                          <dt>Most recent assignment effective date</dt>
                          <dd>{position.effectiveDate ?? "Not supplied"}</dd>
                          <dt>Hold status</dt>
                          <dd>{position.holdStatus ?? "No hold reported"}</dd>
                        </dl>

                        <h4>Match reasons</h4>
                        {position.matchReasons.length ? (
                          <ul>
                            {position.matchReasons.map((reason, index) => (
                              <li key={`${reason}:${index}`}>{reason}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>No detailed match reasons supplied.</p>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}

              {nextCursor && (
                <button
                  type="button"
                  className="secondary-button"
                  disabled={loading}
                  onClick={loadMore}
                >
                  {loading ? "Loading…" : "Load more"}
                </button>
              )}

              <span className="sr-only">
                Current page cursor: {pageCursor ?? "first page"}
              </span>
            </div>
          )}

          {/*
           * NOTE: scopeRules is intentionally NOT rendered here in any form.
           * It is API plumbing passed to the server via query parameters.
           * Displaying raw config data violates DP-12 and DP-5.
           * (OI-S754-C54-SCOPE-JSON-01)
           */}
        </div>
      </section>
    </div>
  );
}

/*
 * DOMAIN COMPLIANCE CHECK
 * - DP-3: The locked "population change does not amend this plan" notice is
 *   always rendered, keeping governed scope rules visibly separate from
 *   runtime population.
 * - DP-5: Complexity (scope rule details, raw config) is hidden through
 *   progressive disclosure; raw data is never shown.
 * - DP-12/XC-1: No economic semantics are inferred from raw config. scopeRules
 *   is API plumbing only and is never rendered in any form.
 * - XC-3: DataFreshnessIndicator is the single freshness signal source;
 *   this component never computes staleness independently.
 * - XC-7/DP-14: Match status is always communicated as text; CSS classes
 *   reinforce state visually but never define it.
 * - OI-S754-C54-LOADMORE-ABORT-01: loadMore AbortController stored in
 *   loadMoreControllerRef; aborted in close/unmount useEffect cleanup.
 * - OI-S754-C54-SCOPE-JSON-01: Raw JSON scopeRules debug block removed
 *   entirely.
 */

/*
 * SESSION 6 MIGRATION FLAGS
 * - Population meta endpoint shape (resolvedAt, additionCount, removalCount,
 *   freshnessStatus) — Session 6 API contract gap; component normalizes both
 *   camelCase and snake_case field names defensively.
 */

/*
 * ACCESSIBILITY NOTES
 * - Drawer is a dialog with aria-modal="true" and aria-labelledby.
 * - Focus trap cycles between all focusable elements within the drawer.
 * - On open: focus moves to the close button via requestAnimationFrame.
 * - On close: focus returns to the element that triggered the drawer.
 * - Escape key closes the drawer from any focused element.
 * - Loading skeletons carry aria-busy="true".
 * - Error states use role="alert".
 * - Match status badges communicate state in text; color is reinforcement only.
 * - Expand/collapse buttons carry aria-expanded.
 */
